<script setup lang="ts">
import { Loader2, ZoomIn, ZoomOut } from 'lucide-vue-next'
import type { PdfScrollTarget, usePdfScroll } from '~/composables/usePdfScroll'
import { findPdfTextMatch } from '~/lib/pdfTextMatch'
import { findHighlightPage } from '~/lib/pdfHighlightSearch'

const { t } = useI18n()
const props = defineProps<{
  src: string
}>()

const emit = defineEmits<{
  'text-selected': [text: string]
}>()

const pdfScroll = inject<ReturnType<typeof usePdfScroll> | null>('pdfScroll', null)

const scrollContainer = ref<HTMLDivElement | null>(null)
const pageRefs = ref<Map<number, { canvas: HTMLCanvasElement; textLayer: HTMLDivElement; highlightLayer: HTMLDivElement }>>(new Map())

const loading = ref(true)
const error = ref<string | null>(null)
const totalPages = ref(0)
const scale = ref(1.0)
const renderedPages = ref<Set<number>>(new Set())

let pdfDoc: any = null
// True once loadPdf has fully finished (page placeholders in the DOM), success OR failure — what the docs
// page's agent-highlight wait polls, so a request made right after navigation is applied, not lost.
const ready = ref(false)
const renderingPages = new Map<number, Promise<void>>()
// A highlightBox request that arrived before the PDF finished loading, applied once loadPdf completes.
let pendingBox: { page: number; box: [number, number, number, number] } | null = null

async function loadPdf() {
  ready.value = false
  loading.value = true
  error.value = null
  renderedPages.value = new Set()
  pageRefs.value.clear()
  renderingPages.clear()
  clearHighlights()

  try {
    const pdfjsLib = await import('pdfjs-dist')

    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString()

    // pdfjs v6 no longer accepts a bare URL string — pass { url }. The asset URLs point at the
    // pdfjs-dist files served via nitro.publicAssets (nuxt.config): `wasmUrl` is what lets scanned
    // JBIG2 / JPEG2000 pages decode (without it they render blank); cMap/standardFont cover CJK text
    // and embedded-font fallbacks.
    const loadingTask = pdfjsLib.getDocument({
      url: props.src,
      wasmUrl: '/pdfjs/wasm/',
      cMapUrl: '/pdfjs/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: '/pdfjs/standard_fonts/',
    })
    pdfDoc = await loadingTask.promise
    totalPages.value = pdfDoc.numPages
  } catch (err: any) {
    // See `app/utils/viewerError.ts` for why the raw message is no longer the user-facing string.
    console.error('[PdfViewer] Failed to load PDF:', { src: props.src, status: err?.status, name: err?.name, err })
    error.value = viewerErrorMessage(err, t)
  } finally {
    loading.value = false
  }

  // After DOM updates with page placeholders, start observing
  await nextTick()
  observePages()

  pdfScroll?.register(async (target: PdfScrollTarget) => {
    await scrollToTarget(target)
  })

  // A highlight requested while the PDF was still loading (e.g. entering Split auto-selects a doubt
  // before pdf.js is ready) — apply it now.
  if (pendingBox) {
    const t = pendingBox
    pendingBox = null
    await highlightBox(t.page, t.box)
  }
  ready.value = true
}

// ── Lazy rendering with IntersectionObserver ──
let observer: IntersectionObserver | null = null

function observePages() {
  if (observer) observer.disconnect()
  if (!scrollContainer.value) return

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const pageNum = parseInt((entry.target as HTMLElement).dataset.page || '0')
        if (pageNum > 0 && !renderedPages.value.has(pageNum)) {
          renderPage(pageNum)
        }
      }
    },
    { root: scrollContainer.value, rootMargin: '200px 0px' },
  )

  // Observe all page wrappers
  const wrappers = scrollContainer.value.querySelectorAll('[data-page]')
  wrappers.forEach(el => observer!.observe(el))
}

async function renderPage(pageNum: number) {
  if (!pdfDoc || renderedPages.value.has(pageNum)) return
  if (renderingPages.has(pageNum)) return renderingPages.get(pageNum)

  const promise = doRenderPage(pageNum)
  renderingPages.set(pageNum, promise)
  try { await promise } finally { renderingPages.delete(pageNum) }
}

async function doRenderPage(pageNum: number) {
  const page = await pdfDoc.getPage(pageNum)
  const viewport = page.getViewport({ scale: scale.value })

  const wrapper = scrollContainer.value?.querySelector(`[data-page="${pageNum}"]`) as HTMLElement
  if (!wrapper) return

  let canvas = wrapper.querySelector('canvas') as HTMLCanvasElement
  if (!canvas) {
    canvas = document.createElement('canvas')
    canvas.className = 'block'
    wrapper.appendChild(canvas)
  }

  let textLayer = wrapper.querySelector('.textLayer') as HTMLDivElement
  if (!textLayer) {
    textLayer = document.createElement('div')
    textLayer.className = 'textLayer'
    wrapper.appendChild(textLayer)
  }

  let highlightLayer = wrapper.querySelector('.pdf-highlight-layer') as HTMLDivElement
  if (!highlightLayer) {
    highlightLayer = document.createElement('div')
    highlightLayer.className = 'pdf-highlight-layer'
    wrapper.appendChild(highlightLayer)
  }

  // Clamp the canvas backing store. Mobile browsers (esp. Android Chrome) render a canvas BLANK/WHITE
  // once it exceeds a max area/side — hit easily on high-DPR phones with large scanned pages (scanner
  // apps size the PDF page to the photo's pixels, so viewport × devicePixelRatio blows past the limit).
  // Cap each side + total area; the CSS size is unchanged so the page still displays full-size, just at
  // a slightly lower internal resolution for oversized pages. No-op for normal-sized pages.
  const dpr = window.devicePixelRatio || 1
  const MAX_CANVAS_SIDE = 4096
  const MAX_CANVAS_AREA = 4096 * 4096
  let outputScale = Math.min(dpr, MAX_CANVAS_SIDE / viewport.width, MAX_CANVAS_SIDE / viewport.height)
  if (viewport.width * viewport.height * outputScale * outputScale > MAX_CANVAS_AREA) {
    outputScale = Math.sqrt(MAX_CANVAS_AREA / (viewport.width * viewport.height))
  }
  outputScale = Math.max(0.1, outputScale)

  wrapper.style.width = `${viewport.width}px`
  wrapper.style.height = `${viewport.height}px`
  wrapper.style.setProperty('--total-scale-factor', String(scale.value))

  canvas.width = Math.max(1, Math.floor(viewport.width * outputScale))
  canvas.height = Math.max(1, Math.floor(viewport.height * outputScale))
  canvas.style.width = `${viewport.width}px`
  canvas.style.height = `${viewport.height}px`

  textLayer.style.width = `${viewport.width}px`
  textLayer.style.height = `${viewport.height}px`
  textLayer.style.setProperty('--total-scale-factor', String(scale.value))

  highlightLayer.style.width = `${viewport.width}px`
  highlightLayer.style.height = `${viewport.height}px`

  const ctx = canvas.getContext('2d')!
  ctx.scale(outputScale, outputScale)
  const renderTask = page.render({ canvasContext: ctx, viewport })
  try {
    await renderTask.promise
  } catch (err: any) {
    if (err?.name === 'RenderingCancelledException') return
    console.error(`[PdfViewer] Render failed for page ${pageNum}:`, err)
    return
  }

  textLayer.innerHTML = ''
  highlightLayer.innerHTML = ''
  const textContent = await page.getTextContent()
  const { TextLayer } = await import('pdfjs-dist')
  const tl = new TextLayer({
    textContentSource: textContent,
    container: textLayer,
    viewport,
  })
  await tl.render()

  pageRefs.value.set(pageNum, { canvas, textLayer, highlightLayer })
  renderedPages.value = new Set([...renderedPages.value, pageNum])
}

// ── Re-render all visible pages on zoom ──
async function reRenderAll() {
  const prev = new Set(renderedPages.value)
  renderedPages.value = new Set()
  renderingPages.clear()
  pageRefs.value.clear()
  const wrappers = scrollContainer.value?.querySelectorAll('[data-page]')
  wrappers?.forEach(el => {
    ;(el as HTMLElement).innerHTML = ''
  })
  await nextTick()
  // Re-render previously rendered pages
  for (const pageNum of prev) {
    await renderPage(pageNum)
  }
}

// ── Text selection ──
function handleMouseUp() {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed) return
  const text = selection.toString().trim()
  if (text) emit('text-selected', text)
}

// ── Zoom ──
const MIN_ZOOM = 0.4
const MAX_ZOOM = 3
// The slider mirrors `scale` for a smooth thumb; `scale` only updates once a (debounced) re-render runs.
const zoomSlider = ref(1)
watch(scale, (v) => { zoomSlider.value = v })

// Zoom while KEEPING the scroll position: the page stack scales uniformly, so preserve the same
// fractional scroll offset across the re-render (this is what was jumping before).
async function applyZoom(target: number) {
  const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(target * 100) / 100))
  if (clamped === scale.value) return
  const el = scrollContainer.value
  const denomBefore = el ? el.scrollHeight - el.clientHeight : 0
  const frac = el && denomBefore > 0 ? el.scrollTop / denomBefore : 0
  scale.value = clamped
  await reRenderAll()
  await nextTick()
  if (el) el.scrollTop = frac * (el.scrollHeight - el.clientHeight)
}

function zoomIn() { applyZoom(scale.value + 0.2) }
function zoomOut() { applyZoom(scale.value - 0.2) }

// Smooth slider: reflect the thumb immediately, debounce the (heavy) re-render.
let zoomDebounce: ReturnType<typeof setTimeout> | null = null
function onZoomSlider(e: Event) {
  const v = (e.target as HTMLInputElement).valueAsNumber
  zoomSlider.value = v
  if (zoomDebounce) clearTimeout(zoomDebounce)
  zoomDebounce = setTimeout(() => applyZoom(v), 110)
}

// ── Test: select random lines ──
function selectRandomLines() {
  // Pick a random rendered page
  const rendered = [...renderedPages.value]
  if (rendered.length === 0) return
  const pageNum = rendered[Math.floor(Math.random() * rendered.length)]!
  const ref = pageRefs.value.get(pageNum)
  if (!ref) return

  const spans = Array.from(ref.textLayer.querySelectorAll('span'))
  if (spans.length === 0) return

  const start = Math.floor(Math.random() * Math.max(1, spans.length - 3))
  const end = Math.min(start + 2 + Math.floor(Math.random() * 3), spans.length - 1)

  const range = document.createRange()
  range.setStart(spans[start]!.firstChild || spans[start]!, 0)
  const endNode = spans[end]!.firstChild || spans[end]!
  range.setEnd(endNode, endNode.textContent?.length || 0)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)

  const text = selection?.toString().trim()
  if (text) emit('text-selected', text)
}

// ── Highlight text across all pages ──
const highlightEls = ref<HTMLElement[]>([])

function clearHighlights() {
  for (const el of highlightEls.value) el.remove()
  highlightEls.value = []
  for (const ref of pageRefs.value.values()) {
    ref.highlightLayer.innerHTML = ''
  }
}

async function scrollToTarget(target: PdfScrollTarget) {
  if (!pdfDoc) return
  if (target.clearExisting !== false) clearHighlights()

  const page = typeof target.page === 'number' ? target.page : null
  const texts = (target.texts || []).map(t => t.trim()).filter(Boolean)

  if (texts.length > 0) {
    const found = await highlightTargetTexts(texts, page, Boolean(target.searchAdjacentPages))
    if (found) return
  }

  if (page) await scrollToPage(page)
}

// Agent `highlight_text` (MAR-382). `page` is optional in the tool schema: without it the whole document
// is searched; with it, it is tried first. Pages are probed through pdf.js' text content (no canvas
// render), and only a page that matches is rendered and painted.
async function highlightText(text: string | null | undefined, page?: number) {
  if (!pdfDoc || !text) return false
  clearHighlights()
  const doc = pdfDoc
  const hit = await findHighlightPage(page, totalPages.value, async (pageNum) => {
    if (doc !== pdfDoc) return false
    const content = await (await doc.getPage(pageNum)).getTextContent()
    const pageText = content.items.map((item: any) => item.str ?? '').join(' ')
    return findPdfTextMatch(pageText, text) !== null && await highlightPageText(text, pageNum)
  })
  return hit !== null
}

async function highlightTargetTexts(texts: string[], page: number | null, searchAdjacentPages: boolean) {
  const pages = pagesToSearch(page, searchAdjacentPages)
  for (const pageNum of pages) {
    for (const text of texts) {
      const found = await highlightPageText(text, pageNum)
      if (found) return true
    }
  }
  return false
}

function pagesToSearch(page: number | null, includeAdjacent: boolean) {
  if (!page || page < 1 || page > totalPages.value) return []
  const pages = includeAdjacent ? [page, page - 1, page + 1] : [page]
  return [...new Set(pages.filter(p => p >= 1 && p <= totalPages.value))]
}

async function highlightPageText(text: string, targetPage: number) {
  if (!pdfDoc || !text) return false

  await renderPage(targetPage)
  const ref = pageRefs.value.get(targetPage)
  if (!ref) return false

  const spans = Array.from(ref.textLayer.querySelectorAll('span'))
  if (spans.length === 0) return false

  let accumulated = ''
  const spanMap: Array<{ span: HTMLElement; startIdx: number; endIdx: number }> = []
  for (const span of spans) {
    const t = span.textContent || ''
    const startIdx = accumulated.length
    accumulated += t + ' '
    spanMap.push({ span: span as HTMLElement, startIdx, endIdx: startIdx + t.length })
  }

  const match = findPdfTextMatch(accumulated, text)
  if (!match) return false

  const matchingSpans = spanMap.filter(s => s.endIdx > match.start && s.startIdx < match.end)
  if (matchingSpans.length === 0) return false

  const wrapper = scrollContainer.value?.querySelector(`[data-page="${targetPage}"]`) as HTMLElement
  wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  await new Promise(r => setTimeout(r, 300))

  const layerRect = ref.highlightLayer.getBoundingClientRect()
  const layerWidth = layerRect.width
  const layerHeight = layerRect.height
  let painted = false

  for (const { span } of matchingSpans) {
    const rect = span.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) continue

    const left = rect.left - layerRect.left
    const top = rect.top - layerRect.top
    if (
      left < -2
      || top < -2
      || left + rect.width < -2
      || top + rect.height < -2
      || left > layerWidth + 2
      || top > layerHeight + 2
    ) {
      continue
    }
    const clampedLeft = Math.max(0, left)
    const clampedTop = Math.max(0, top)
    const width = Math.min(rect.width, layerWidth - clampedLeft)
    const height = Math.min(rect.height, layerHeight - clampedTop)
    if (width <= 0 || height <= 0) continue

    const highlight = document.createElement('div')
    highlight.className = 'pdf-highlight'
    highlight.style.position = 'absolute'
    highlight.style.left = `${clampedLeft}px`
    highlight.style.top = `${clampedTop}px`
    highlight.style.width = `${width}px`
    highlight.style.height = `${height}px`
    ref.highlightLayer.appendChild(highlight)
    highlightEls.value.push(highlight)
    painted = true
  }

  if (!painted) return false

  matchingSpans[0]?.span.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

// Highlight a normalized region box `[ymin, xmin, ymax, xmax]` (0-1000) on a given page, and scroll it
// into view. Used by the OCR-review split view to point at a doubted line on the original scan (the box
// spans the full line width). Coordinates are relative to the page, so this is renderer-agnostic — no
// text search. Returns whether it painted.
async function highlightBox(page: number, box: [number, number, number, number] | null) {
  if (!box) return false
  // Requested before the PDF finished loading → remember it; loadPdf applies it once ready.
  if (!pdfDoc) { pendingBox = { page, box }; return false }
  if (page < 1 || page > totalPages.value) return false
  clearHighlights()
  await renderPage(page)
  const ref = pageRefs.value.get(page)
  if (!ref) return false

  const wrapper = scrollContainer.value?.querySelector(`[data-page="${page}"]`) as HTMLElement
  wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  await new Promise(r => setTimeout(r, 300))

  const layerRect = ref.highlightLayer.getBoundingClientRect()
  const [ymin, xmin, ymax, xmax] = box
  const left = (Math.max(0, Math.min(1000, xmin)) / 1000) * layerRect.width
  const width = (Math.max(0, Math.min(1000, xmax - xmin)) / 1000) * layerRect.width
  let top = (Math.max(0, Math.min(1000, ymin)) / 1000) * layerRect.height
  let height = (Math.max(0, Math.min(1000, ymax - ymin)) / 1000) * layerRect.height
  if (width <= 0 || height <= 0) return false
  // A little vertical breathing room so the band doesn't clip the tops/tails of the handwriting.
  const pad = 6
  top = Math.max(0, top - pad)
  height = Math.min(layerRect.height - top, height + pad * 2)

  const highlight = document.createElement('div')
  highlight.className = 'pdf-line-highlight'
  highlight.style.position = 'absolute'
  highlight.style.left = `${left}px`
  highlight.style.top = `${top}px`
  highlight.style.width = `${width}px`
  highlight.style.height = `${height}px`
  ref.highlightLayer.appendChild(highlight)
  highlightEls.value.push(highlight)

  highlight.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

async function scrollToPage(pageNum: number) {
  if (!pdfDoc || pageNum < 1 || pageNum > totalPages.value) return
  if (!renderedPages.value.has(pageNum)) {
    await renderPage(pageNum)
    await new Promise(r => setTimeout(r, 100))
  }
  const wrapper = scrollContainer.value?.querySelector(`[data-page="${pageNum}"]`) as HTMLElement
  wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

defineExpose({ selectRandomLines, isReady: () => ready.value, highlightText, highlightBox, clearHighlights, scrollToPage, scrollToTarget })

onMounted(() => loadPdf())

watch(() => props.src, () => {
  scale.value = 1.0
  loadPdf()
})

onUnmounted(() => {
  observer?.disconnect()
  pdfScroll?.unregister()
})

// ── Approximate page height for placeholders ──
const placeholderHeight = ref(800)

async function getFirstPageHeight() {
  if (!pdfDoc) return
  const page = await pdfDoc.getPage(1)
  const vp = page.getViewport({ scale: scale.value })
  placeholderHeight.value = vp.height
}

watch(totalPages, () => getFirstPageHeight())
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Toolbar -->
    <div class="flex items-center justify-between px-2 py-1 border-b shrink-0 bg-muted/30">
      <span class="text-[11px] text-muted-foreground tabular-nums">
        {{ totalPages }} {{ totalPages === 1 ? t('common.page') : t('common.pages') }}
      </span>

      <div class="flex items-center gap-1">
        <button
          type="button"
          class="h-6 px-1.5 text-[10px] rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          title="Select random lines (test)"
          @click="selectRandomLines"
        >
          Test
        </button>
        <button
          type="button"
          class="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          title="Alejar"
          @click="zoomOut"
        >
          <ZoomOut class="h-3.5 w-3.5" />
        </button>
        <input
          type="range"
          class="pdf-zoom-slider w-24"
          :min="MIN_ZOOM"
          :max="MAX_ZOOM"
          step="0.05"
          :value="zoomSlider"
          aria-label="Zoom"
          @input="onZoomSlider"
        >
        <span class="text-[10px] text-muted-foreground tabular-nums w-10 text-center">
          {{ Math.round(zoomSlider * 100) }}%
        </span>
        <button
          type="button"
          class="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          title="Acercar"
          @click="zoomIn"
        >
          <ZoomIn class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>

    <!-- Scrollable page stack -->
    <div ref="scrollContainer" class="flex-1 overflow-auto bg-muted/20" @mouseup="handleMouseUp">
      <!-- Loading -->
      <div v-if="loading" class="h-full flex items-center justify-center">
        <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="h-full flex items-center justify-center px-4">
        <p class="text-sm text-destructive text-center">{{ error }}</p>
      </div>

      <!-- All pages stacked vertically -->
      <div v-else class="flex flex-col items-center gap-2 p-4">
        <div
          v-for="pageNum in totalPages"
          :key="pageNum"
          :data-page="pageNum"
          class="relative shadow-sm bg-background"
          :style="{ minHeight: `${placeholderHeight}px` }"
        />
      </div>
    </div>
  </div>
</template>

<style>
@reference "~/assets/css/tailwind.css";
.textLayer {
  position: absolute;
  text-align: initial;
  inset: 0;
  overflow: clip;
  opacity: 1;
  line-height: 1;
  text-size-adjust: none;
  forced-color-adjust: none;
  transform-origin: 0 0;
  caret-color: CanvasText;
  z-index: 1;
  --min-font-size: 1;
  --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
  --min-font-size-inv: calc(1 / var(--min-font-size));
}
.textLayer :is(span, br) {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}
.textLayer > :not(.markedContent),
.textLayer .markedContent span:not(.markedContent) {
  z-index: 1;
  --font-height: 0;
  font-size: calc(var(--text-scale-factor) * var(--font-height));
  --scale-x: 1;
  --rotate: 0deg;
  transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv));
}
.textLayer .markedContent {
  display: contents;
}
.textLayer span[role="img"] {
  user-select: none;
  cursor: default;
}
.textLayer ::selection {
  @apply bg-primary/30;
}
.textLayer br::selection {
  background: transparent;
}
.textLayer .endOfContent {
  display: block;
  position: absolute;
  inset: 100% 0 0;
  z-index: 0;
  cursor: default;
  user-select: none;
}
.pdf-highlight-layer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;
}
.pdf-highlight {
  background-color: color-mix(in srgb, var(--color-warning) 40%, transparent);
  border-bottom: 2px solid var(--color-warning-ink);
  border-radius: 2px;
  pointer-events: none;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent);
  animation: highlight-pulse 1.5s ease-in-out;
}
@keyframes highlight-pulse {
  0% { background-color: color-mix(in srgb, var(--color-warning) 70%, transparent); box-shadow: 0 0 8px color-mix(in srgb, var(--color-warning) 50%, transparent); }
  50% { background-color: color-mix(in srgb, var(--color-warning) 30%, transparent); box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent); }
  100% { background-color: color-mix(in srgb, var(--color-warning) 40%, transparent); box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent); }
}
/* OCR-review line band: a full-width tint with a soft ring (no hard bottom rule); fades on hover so you
   can read the ink underneath. pointer-events:auto (re-enabled over the layer) is what lets hover fire. */
.pdf-line-highlight {
  background-color: color-mix(in srgb, var(--color-warning) 38%, transparent);
  border-radius: 3px;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 45%, transparent);
  pointer-events: auto;
  transition: opacity 0.15s ease;
}
.pdf-line-highlight:hover {
  opacity: 0.3;
}
/* Compact zoom slider, brand-tinted. */
.pdf-zoom-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-accent-strong) 30%, transparent);
  cursor: pointer;
}
.pdf-zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--color-accent-strong);
  border: 2px solid var(--color-background);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-accent-strong) 40%, transparent);
}
.pdf-zoom-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: 2px solid var(--color-background);
  border-radius: 999px;
  background: var(--color-accent-strong);
}
</style>
